import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, MapPin, Wind, Trash2, Image as ImageIcon, Boxes, Loader2 } from "lucide-react";
import { api, errMsg } from "./api";
import { NewProjectDialog } from "./NewProjectDialog";

const Stat = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-zinc-400"><Icon className="h-3.5 w-3.5" />{children}</span>
);

const ProjectCard = ({ p, onDelete, i }) => (
  <div data-testid={`project-card-${p.id}`} className="group relative rounded-lg border border-zinc-800 bg-[#121215] p-5 transition-colors duration-200 hover:border-sky-500/50 animate-slide-up" style={{ animationDelay: `${i * 50}ms` }}>
    <Link to={`/projects/${p.id}`} data-testid={`open-project-${p.id}`} className="block">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400">{p.building_type}</div>
          <h3 className="mt-1 font-[Manrope] text-lg font-semibold text-white">{p.name}</h3>
          {p.client && <p className="text-sm text-zinc-500">{p.client}</p>}
        </div>
        <Building2 className="h-5 w-5 text-zinc-600 group-hover:text-sky-400" />
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {p.location && <Stat icon={MapPin}>{p.location}</Stat>}
        <Stat icon={Wind}>{p.basic_wind_speed} m/s · {p.building_height_m} m</Stat>
        <Stat icon={ImageIcon}>{p.elevation ? `${p.zones?.length || 0} zones` : "No elevation"}</Stat>
        <Stat icon={Boxes}>{p.design_count} systems</Stat>
      </div>
    </Link>
    <button data-testid={`delete-project-${p.id}`} onClick={() => onDelete(p)} className="absolute bottom-4 right-4 rounded p-1.5 text-zinc-600 opacity-0 transition-opacity duration-200 hover:text-red-400 group-hover:opacity-100">
      <Trash2 className="h-4 w-4" />
    </button>
  </div>
);

export default function Dashboard() {
  const [projects, setProjects] = useState(null);
  const navigate = useNavigate();
  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Delete project "${p.name}" and all its systems?`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success("Project deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">Workspace</div>
          <h1 className="mt-1 font-[Manrope] text-3xl font-extrabold tracking-tight md:text-4xl">Facade Projects</h1>
        </div>
        <NewProjectDialog onCreated={(p) => navigate(`/projects/${p.id}`)} />
      </div>
      {projects === null ? (
        <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
      ) : projects.length === 0 ? (
        <div data-testid="empty-projects" className="rounded-lg border border-dashed border-zinc-800 p-16 text-center text-zinc-500">
          No projects yet. Create one to start mapping your facade.
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3" data-testid="projects-grid">
          {projects.map((p, i) => <ProjectCard key={p.id} p={p} i={i} onDelete={remove} />)}
        </div>
      )}
    </div>
  );
}
